import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Boxes, Search, Loader2, AlertTriangle, ArrowDownCircle, ArrowUpCircle, History } from 'lucide-react';
import { toast } from 'sonner';

const fmtQty = (v) => v == null ? '-' : (Number.isInteger(Number(v)) ? String(v) : Number(v).toFixed(2).replace(/\.?0+$/, ''));

export default function ProductStockPage() {
  const [stock, setStock] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [onlyNegative, setOnlyNegative] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);

  const fetchStock = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...(onlyNegative ? { only_negative: true } : {}), ...(search ? { search } : {}) };
      const [s, m] = await Promise.all([
        axios.get(`${API}/product-stock`, { params }),
        axios.get(`${API}/product-stock/movements`, { params: { limit: 100 } }),
      ]);
      setStock(s.data);
      setMovements(m.data);
    } catch { toast.error('Errore caricamento scorte prodotti'); }
    finally { setLoading(false); }
  }, [search, onlyNegative]);

  useEffect(() => { fetchStock(); }, [fetchStock]);

  const filteredMovements = selectedCode
    ? movements.filter(m => m.product_code === selectedCode)
    : movements;

  const totalItems = stock.length;
  const totalNegative = stock.filter(s => s.current_qty < 0 || s.current_colli < 0).length;
  const totalUnits = stock.reduce((sum, s) => sum + (s.current_colli || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Boxes size={24} className="text-primary" />
            Scorte Prodotti Finiti
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Aggiornate automaticamente dal DDT (scarico) e dagli annullamenti (storno)
          </p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Articoli tracciati</p>
              <p className="text-2xl font-bold">{totalItems}</p>
            </div>
            <Boxes className="text-primary" size={30} />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Giacenze negative</p>
              <p className={`text-2xl font-bold ${totalNegative ? 'text-red-600' : ''}`}>{totalNegative}</p>
            </div>
            <AlertTriangle className={totalNegative ? 'text-red-500' : 'text-muted-foreground'} size={30} />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Colli totali</p>
              <p className={`text-2xl font-bold ${totalUnits < 0 ? 'text-red-600' : 'text-primary'}`}>{totalUnits}</p>
            </div>
            <History className="text-muted-foreground" size={30} />
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Cerca per codice o descrizione..."
            className="pl-9 h-9"
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="stock-search"
          />
        </div>
        <Button
          variant={onlyNegative ? 'default' : 'outline'}
          size="sm"
          onClick={() => setOnlyNegative(!onlyNegative)}
          data-testid="only-negative-btn"
        >
          <AlertTriangle size={13} className="mr-1.5" />
          Solo negative
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Stock list */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
                Giacenza articoli
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="animate-spin text-primary" /></div>
              ) : stock.length === 0 ? (
                <p className="text-center py-8 text-sm text-muted-foreground">
                  Nessuna scorta rilevata. Le giacenze si popolano automaticamente alla prima generazione DDT.
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-muted/30 border-b">
                    <tr className="text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="text-left px-4 py-2">Codice</th>
                      <th className="text-left px-4 py-2">Descrizione</th>
                      <th className="text-right px-4 py-2">Qtà</th>
                      <th className="text-right px-4 py-2">Colli</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {stock.map(s => {
                      const neg = s.current_qty < 0 || s.current_colli < 0;
                      return (
                        <tr
                          key={s.id}
                          className={`border-b hover:bg-muted/30 cursor-pointer ${selectedCode === s.product_code ? 'bg-primary/5' : ''}`}
                          onClick={() => setSelectedCode(selectedCode === s.product_code ? null : s.product_code)}
                          data-testid={`stock-row-${s.product_code}`}
                        >
                          <td className="px-4 py-2 font-mono font-semibold text-primary">{s.product_code}</td>
                          <td className="px-4 py-2 text-muted-foreground">{s.description || '-'}</td>
                          <td className={`px-4 py-2 text-right font-bold ${neg ? 'text-red-600' : ''}`}>{fmtQty(s.current_qty)}</td>
                          <td className={`px-4 py-2 text-right font-bold ${neg ? 'text-red-600' : ''}`}>{s.current_colli}</td>
                          <td className="px-4 py-2 text-right">{neg && <AlertTriangle size={13} className="text-red-500" />}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Movements */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground flex items-center justify-between">
              <span>Movimenti recenti {selectedCode && <span className="text-primary">— {selectedCode}</span>}</span>
              {selectedCode && (
                <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setSelectedCode(null)}>Reset</Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 max-h-[500px] overflow-y-auto">
            {filteredMovements.length === 0 ? (
              <p className="text-center py-8 text-sm text-muted-foreground">Nessun movimento</p>
            ) : (
              <ul className="divide-y">
                {filteredMovements.slice(0, 50).map(m => {
                  const isOut = m.qty_delta < 0 || m.colli_delta < 0;
                  const icon = isOut ? <ArrowDownCircle size={16} className="text-red-500" /> : <ArrowUpCircle size={16} className="text-emerald-500" />;
                  const dtLabel = new Date(m.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
                  return (
                    <li key={m.id} className="px-4 py-2.5 text-xs">
                      <div className="flex items-start gap-2">
                        {icon}
                        <div className="flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-mono font-semibold">{m.product_code}</span>
                            <span className={`font-bold ${isOut ? 'text-red-600' : 'text-emerald-600'}`}>
                              {m.qty_delta > 0 ? '+' : ''}{fmtQty(m.qty_delta)} qty · {m.colli_delta > 0 ? '+' : ''}{m.colli_delta} colli
                            </span>
                          </div>
                          <p className="text-muted-foreground mt-0.5">{m.reason || m.movement_type}</p>
                          {m.ddt_number && (
                            <p className="text-muted-foreground text-[10px] mt-0.5">DDT {m.ddt_number} · {dtLabel}</p>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
